// Shows blueprint steps and actions.

import { useState } from 'react';
import { Check, ChevronDown, CircleFadingPlus, CornerDownLeft, ListTree, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CollapseBody } from './collapse-toggle';
import type { Blueprint } from './types';

function StepRow({
  index,
  title,
  description,
  open,
  onToggle,
}: {
  index: number;
  title: string;
  description: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex w-full flex-col rounded-2xl bg-gray-100 px-3 py-2">
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 leading-[18px]">
          <span className="font-mono text-[12px] uppercase text-gray-800">{index + 1}.</span>
          <span className="truncate text-[13px] font-medium text-gray-900">{title}</span>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggle}
          className="text-gray-900 hover:bg-white"
          aria-label={open ? 'Collapse step' : 'Expand step'}
        >
          <ChevronDown className={cn('size-[14px] transition-transform duration-300', open && 'rotate-180')} />
        </Button>
      </div>
      <CollapseBody open={open}>
        <p className="pt-1.5 text-[11px] leading-4 text-text-body">{description}</p>
      </CollapseBody>
    </div>
  );
}

/** Pink pill — "Refined Blueprint" marks a refined card; "Refine Blueprint" labels the refine input. */
function RefineChip({ label = 'Refined Blueprint' }: { label?: string }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-pink-50 px-2.5 py-1 text-[12px] leading-4 text-pink-500">
      <CircleFadingPlus className="size-3" />
      {label}
    </span>
  );
}

interface BlueprintCardProps {
  blueprint: Blueprint;
  /** From Settings → Sentinel Workspace, so it matches the backend; null while it loads. */
  refineLimitHit: boolean | null;
  refineOpen: boolean;
  onRefineOpenChange: (open: boolean) => void;
  onRefineSubmit: (feedback: string) => void;
  /** Only reachable once the refines run out (`refineLimitHit`), so a caller with no limit omits it. */
  onReject?: () => void;
  onGenerate: () => void;
}

export function BlueprintCard({
  blueprint,
  refineLimitHit,
  refineOpen,
  onRefineOpenChange,
  onRefineSubmit,
  onReject,
  onGenerate,
}: BlueprintCardProps) {
  const [feedback, setFeedback] = useState('');
  // One step open at a time — the first is expanded until another is picked.
  const [openStep, setOpenStep] = useState(0);

  const submitFeedback = () => {
    if (!feedback.trim()) return;
    onRefineSubmit(feedback.trim());
    setFeedback('');
  };

  return (
    // Capped so a long blueprint cannot cover the thread — the steps scroll inside it.
    <div className="flex max-h-96 min-h-0 w-full flex-col gap-4 rounded-2xl border border-gray-500 bg-white/80 p-4 backdrop-blur-[6px]">
      <div className="flex shrink-0 items-center justify-between">
        <div className="flex items-center gap-2.5 text-gray-700">
          <ListTree className="size-[14px]" />
          <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">Suggested Blueprint</span>
        </div>
        {/* Version 1 is the original — anything above means this blueprint was refined. */}
        {blueprint.version > 1 && <RefineChip />}
      </div>

      {/* Scrolls without a bar: the steps already show there is more below. */}
      <div className="flex w-full min-h-0 flex-1 flex-col gap-2 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {blueprint.steps.map((step, index) => (
          <StepRow
            key={step.id}
            index={index}
            title={step.title}
            description={step.description}
            open={openStep === index}
            onToggle={() => setOpenStep((previous) => (previous === index ? -1 : index))}
          />
        ))}
      </div>

      {refineOpen ? (
        <div className="flex w-full shrink-0 flex-col gap-3 rounded-xl border border-gray-300 bg-white p-3">
          <div className="flex items-center justify-between">
            <RefineChip label="Refine Blueprint" />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onRefineOpenChange(false)}
              className="text-gray-900"
              aria-label="Close refine input"
            >
              <X className="size-[14px]" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <input
              autoFocus
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitFeedback();
              }}
              placeholder="Start typing"
              className="min-w-0 flex-1 bg-transparent text-[13px] leading-[18px] text-text-body outline-none placeholder:text-gray-700"
            />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={submitFeedback}
              className="text-gray-900"
              aria-label="Send feedback"
            >
              <CornerDownLeft className="size-[14px]" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-1.5">
          {/* Rejecting replaces refining once the refines run out — never both, never a guess. */}
          {refineLimitHit === true && (
            <Button variant="ghost" onClick={onReject} className="text-[13px] font-normal text-gray-900">
              Reject &amp; Unassign
            </Button>
          )}
          {refineLimitHit === false && (
            <Button variant="outline" onClick={() => onRefineOpenChange(true)} className="text-[13px] font-normal">
              <CircleFadingPlus className="size-[14px]" />
              Refine blueprint
            </Button>
          )}
          <Button onClick={onGenerate} className="text-[13px] font-normal">
            <Check className="size-[14px]" />
            Looks Good, Generate response
          </Button>
        </div>
      )}
    </div>
  );
}
